import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicComponentHostDirective } from '@benefits/shared';
import { NgFormsManager } from '@ngneat/forms-manager';
import { isObservable, Observable, Subscription } from 'rxjs';
import { StepRouterService } from '../../services';
import { FormStepComponent, FormStepItem, FormSteps, STEP_PARAM_KEY } from './multi-step-form.types';

@Component({
  selector: 'benefits-multi-step-form[steps][formManager]',
  templateUrl: './multi-step-form.component.html',
  styleUrls: ['./multi-step-form.component.scss']
})
export class MultiStepFormComponent implements OnInit, AfterViewInit, OnDestroy {
  step?: FormStepItem;
  componentRef?: ComponentRef<FormStepComponent>;

  @Input() steps: FormSteps = { default: '', steps: {} };
  @Input() formManager!: NgFormsManager;

  @ViewChild(DynamicComponentHostDirective) host!: DynamicComponentHostDirective;

  private subscriptions: Subscription[] = [];

  /**
   * Callback to unsubscribe from form when dynamic form component is cleared
   */
  private formUnsubscriber?: () => void;

  private viewInit = false;

  private currentKey?: string;

  constructor(
    private stepRouter: StepRouterService,
    private changeDetectorRef: ChangeDetectorRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  get disableNext() {
    return !this.componentRef?.instance?.form?.valid;
  }

  ngOnInit(): void {
    this.step = this.steps.steps[this.steps.default];
    this.subscriptions.push(this.route.params.subscribe((params) => this.handlePath(params[STEP_PARAM_KEY])));

    // define each form in case a step is loaded out of order
    Object.entries(this.steps.steps).forEach(([key, step]) => {
      this.formManager.upsert(key, step.component.defineForm());
    });
  }

  ngAfterViewInit(): void {
    this.viewInit = true;
    this.loadStep();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // clean up or else
    this.host.viewContainerRef.clear();
  }

  nextClick() {
    this.normalizePromise(this.componentRef?.instance.next()).then((value) => {
      if (value !== undefined) {
        this.goToPath(value);
      }
    });
  }

  backClick() {
    this.normalizePromise(this.componentRef?.instance.prev()).then((value) => {
      if (value) {
        this.goToPath(value);
      }
    });
  }

  ensurePreviousFormsValid() {
    if (this.componentRef?.instance.ensurePreviousFormsValid) {
      this.normalizePromise(this.componentRef?.instance.ensurePreviousFormsValid()).then((value) => {
        if (value) {
          this.goToPath(value);
        }
      });
    }
  }

  private normalizePromise(
    value?: string | undefined | void | Promise<string | undefined | void> | Observable<string>
  ): Promise<string | undefined | void> {
    if (isObservable(value)) return value.toPromise();
    return Promise.resolve(value);
  }

  private goToPath(path?: string) {
    if (!path || path === '/') {
      this.stepRouter.goToRoot();
    } else if (path.startsWith('/') || path.startsWith('.')) {
      this.router.navigate([path], { queryParamsHandling: 'preserve' });
    } else {
      this.stepRouter.goToStep(path);
    }
  }

  private handlePath(path: string) {
    if (!(path in this.steps.steps)) {
      this.goToPath(this.steps.default);
      this.currentKey = this.steps.default;
      return;
    }

    this.step = this.steps.steps[path];
    this.currentKey = path;
    if (this.viewInit) {
      this.loadStep();
    }
  }

  private loadStep() {
    const ref = this.host.viewContainerRef;

    ref.clear();

    if (this.formUnsubscriber) {
      this.formUnsubscriber();
    }

    if (!this.step || !this.currentKey) return;

    const factory = this.componentFactoryResolver.resolveComponentFactory(this.step.component);

    this.componentRef = ref.createComponent(factory);

    const key = this.currentKey;

    // synchronize form manager with component form
    this.formManager.upsert(key, this.componentRef.instance.form);

    // make sure we've visited the previous form pages.
    this.ensurePreviousFormsValid();

    // tell the component that it is okay to use the form via the formManager now
    if (this.componentRef.instance.onFormReady) {
      this.componentRef.instance.onFormReady();
    }
    this.changeDetectorRef.detectChanges();

    // Capture the key and register unsubscribe callback for when component is cleared
    this.formUnsubscriber = () => {
      this.formManager.unsubscribe(key);
    };
  }
}
